import { API_BASE_URL } from "@/lib/appEnvironment";
import { getActiveAccessToken } from "@/lib/auth";
import { downloadBlob, openOrDownload } from "@/lib/downloadBlob";

export const STORAGE_PROXY_PATH = "/storage/file?";

export function isStorageProxyUrl(url: string | undefined): boolean {
  if (!url) return false;
  const s = url.trim();
  return s.includes(STORAGE_PROXY_PATH) || s.startsWith("storage/file?");
}

/** Whether the value must be fetched via the authenticated storage proxy. */
export function needsAuthenticatedStorageFetch(
  value: string | undefined,
): boolean {
  if (!value) return false;
  const s = value.trim();
  if (!s) return false;
  // Local object/data URLs are already browser-resolvable; never proxy them.
  if (/^(blob|data):/i.test(s)) return false;
  if (isStorageProxyUrl(s)) return true;
  // Public absolute URLs and site-root paths do not need the auth proxy.
  if (/^https?:\/\//i.test(s) || s.startsWith("/")) return false;
  // Remaining values with a path separator are storage object keys.
  return s.includes("/");
}

/** Extract the canonical storage path from a backend proxy URL; passthrough for already-paths. */
export function storagePathFromValue(value: string): string {
  const s = value.trim();
  if (!isStorageProxyUrl(s)) return s;
  // isStorageProxyUrl guarantees a "?" is present, so the query is always parseable.
  const query = s.slice(s.indexOf("?") + 1);
  return new URLSearchParams(query).get("path") ?? s;
}

/**
 * Build the storage download URL against the portal's configured API base.
 * Backend responses may carry another host (e.g. production APP_BASE_URL) while
 * the browser must call the API this build is wired to.
 */
export function resolveStorageFetchUrl(value: string): string {
  const storagePath = storagePathFromValue(value.trim());
  const base = API_BASE_URL.replace(/\/$/, "");
  const prefix =
    /^https?:\/\//i.test(base) || base.startsWith("/") ? base : `/${base}`;
  return `${prefix}${STORAGE_PROXY_PATH}path=${encodeURIComponent(storagePath)}`;
}

export async function fetchStorageBlob(
  url: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const token = getActiveAccessToken();
  const res = await fetch(resolveStorageFetchUrl(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    credentials: "include",
    signal,
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch storage object (${res.status})`);
  }
  return res.blob();
}

/** Download a storage proxy URL as a file (Bearer auth; plain anchor cannot send it). */
export async function downloadStorageUrl(
  url: string,
  filename: string,
): Promise<void> {
  const blob = await fetchStorageBlob(url);
  downloadBlob(blob, filename);
}

/** Open a storage proxy URL in a new tab (Bearer auth; plain window.open cannot send it). */
export async function openStorageUrlInNewTab(url: string): Promise<void> {
  await openOrDownload({
    mode: "view",
    baseName: "file",
    fetchBlob: () => fetchStorageBlob(url),
  });
}
